import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * FF: share of the photo's width on each side that changes photo on phones.
 * 0.3 = left 30% goes back, right 30% goes forward, middle 40% zooms.
 */
const EDGE_TAP_ZONE = 0.3;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * FF: handles a tap on a gallery slide. On phones, a tap on the left or right edge of a photo
   * changes photo instead of zooming (the slideshow loops, so it wraps at either end). Clarity
   * heatmaps showed shoppers tapping the right edge expecting the next photo. Desktop, keyboard
   * clicks, videos and single-photo products keep the stock tap-to-zoom (3D models never route here).
   *
   * @param {number} index - The index of the tapped media.
   * @param {MouseEvent} event - The click event.
   */
  handleSlideClick(index, event) {
    const { slideshow } = this;
    const slide = event.target;

    if (
      isMobileBreakpoint() &&
      event.detail > 0 &&
      slideshow &&
      !slideshow.disabled &&
      (slideshow.slides?.length ?? 0) > 1 &&
      slide instanceof HTMLElement &&
      slide.classList.contains('product-media-container--image')
    ) {
      const { left, width } = slide.getBoundingClientRect();
      const position = width ? (event.clientX - left) / width : 0.5;

      if (position < EDGE_TAP_ZONE) {
        slideshow.previous(event);
        return;
      }

      if (position > 1 - EDGE_TAP_ZONE) {
        slideshow.next(event);
        return;
      }
    }

    this.zoom(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
